import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  AppConfigProvider,
  UserProvider,
  useAppConfig,
  useUser,
} from '@charlava-com/auth';
import { JamcamLayout } from '@charlava-com/ui-react-mui/components';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  CircularProgress,
} from '@mui/material';
import { darkTheme } from './theme';
import { OverlayConfigProvider } from './config/OverlayConfigContext';
import { AudioInputProvider } from './config/AudioInputContext';
import { JamCamStageConfigProvider } from './features/jamcam-stage/StageConfigContext';
import {
  initJamCamAnalytics,
  setAnalyticsAuth,
  trackJC,
} from './utils/analytics';

import Dashboard from './pages/dashboard';
import Logout from './pages/logout';
import SignUpRedirect from './pages/signup';
import { EntitlementGate, DevOverrideProvider } from './core/entitlement';
import {
  LEGACY_SCALE_REDIRECTS,
  MODULE_ROUTES,
} from './core/modules/moduleRoutes';
import { GuidedTour } from './components/GuidedTour';
import { useDisplayName } from './hooks/useDisplayName';

// Camera/TF.js pages and large settings stay out of the initial bundle.
const Cam = lazy(() => import('./pages/cam/Cam'));
const Demo = lazy(() => import('./pages/demo'));
const Tabs = lazy(() =>
  import('./pages/tools/tab-practice/TabLibrary').then((m) => ({
    default: m.TabLibrary,
  }))
);
const Learn = lazy(() => import('./pages/tools'));
const Settings = lazy(() => import('./pages/settings'));
const Account = lazy(() => import('./pages/account'));
const Privacy = lazy(() => import('./pages/privacy'));
const Terms = lazy(() => import('./pages/terms'));
const JamCamAgentPage = lazy(() => import('./pages/agent'));
const PentatonicPractice = lazy(
  () => import('./pages/tools/pentatonic-practice')
);
const ChordPractice = lazy(() => import('./pages/tools/chord-practice'));

function AppRoutes() {
  useAnalyticsBootstrap();
  const [userState, userActions] = useUser();
  const displayName = useDisplayName();
  const { pathname } = useLocation();
  const immersive =
    ['/cam', '/demo', '/learn/scale-practice', '/learn/triads'].includes(
      pathname.replace(/\/$/, '')
    ) ||
    MODULE_ROUTES.some(
      (module) => module.configRoute === pathname.replace(/\/$/, '')
    );
  return (
    <JamcamLayout
      immersive={immersive}
      authenticated={Boolean(userState?.data)}
      authLoading={Boolean(userState?.isLoading)}
      displayName={displayName}
      onSignIn={userActions.signIn}
      onSignUp={userActions.signUp}
      onSignOut={userActions.signOut}
    >
      <OverlayConfigProvider>
        <AudioInputProvider
          enabled={
            pathname.replace(/\/$/, '') !== '/demo' &&
            MODULE_ROUTES.find(
              (module) => module.configRoute === pathname.replace(/\/$/, '')
            )?.usesAudioInput !== false
          }
        >
          <JamCamStageConfigProvider>
            <GuidedTour />
            <Suspense
              fallback={
                <Box
                  role="status"
                  aria-label="Loading module"
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    minHeight: '50vh',
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Routes>
                {MODULE_ROUTES.map(
                  ({
                    id,
                    configRoute,
                    Component,
                    title,
                    requiredEntitlement,
                  }) => (
                    <Route
                      key={id}
                      path={configRoute}
                      element={
                        <EntitlementGate
                          required={requiredEntitlement ?? 'free'}
                          featureName={title}
                        >
                          <Component />
                        </EntitlementGate>
                      }
                    />
                  )
                )}
                {LEGACY_SCALE_REDIRECTS.map(({ from, to }) => (
                  <Route
                    key={from}
                    path={from}
                    element={<Navigate to={to} replace />}
                  />
                ))}
                {/* `/` is auth-gated and redirects to the dashboard. */}
                {/* Anonymous users hit `/dashboard` which is in `protectedRoutes`,
                    triggering federated sign-in via the UserProvider. */}
                <Route
                  path="/"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/agent" element={<JamCamAgentPage />} />
                {/* Public, no auth required. */}
                <Route path="/demo" element={<Demo />} />
                <Route path="/cam" element={<Cam />} />
                <Route path="/learn" element={<Learn />} />
                <Route path="/songs" element={<Tabs />} />
                <Route path="/tabs" element={<Tabs />} />
                <Route
                  path="/tools"
                  element={<Navigate to="/learn" replace />}
                />
                <Route
                  path="/learn/scale-practice"
                  element={
                    <EntitlementGate
                      required="paid"
                      featureName="Scale Practice"
                    >
                      <PentatonicPractice />
                    </EntitlementGate>
                  }
                />

                <Route
                  path="/tools/pentatonic-practice"
                  element={<Navigate to="/learn/scale-practice" replace />}
                />

                <Route
                  path="/tools/guided-solo"
                  element={<Navigate to="/learn/guided-solo" replace />}
                />
                <Route path="/learn/triads" element={<ChordPractice />} />

                <Route
                  path="/tools/triads-practice"
                  element={<Navigate to="/learn/triads" replace />}
                />
                <Route
                  path="/tools/session-engine"
                  element={<Navigate to="/learn" replace />}
                />

                <Route
                  path="/tools/guided-chord-progression"
                  element={<Navigate to="/learn/chord-progression" replace />}
                />
                <Route path="/settings" element={<Settings />} />
                <Route path="/account" element={<Account />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/signup" element={<SignUpRedirect />} />
              </Routes>
            </Suspense>
          </JamCamStageConfigProvider>
        </AudioInputProvider>
      </OverlayConfigProvider>
    </JamcamLayout>
  );
}

function AppWithConfig() {
  const { data: config } = useAppConfig();

  // Initialize analytics as soon as the app config is loaded (apiUrl is
  // required). Safe to call multiple times — the helper is idempotent.
  React.useEffect(() => {
    if (config?.apiUrl) {
      initJamCamAnalytics({ apiUrl: config.apiUrl });
    }
  }, [config?.apiUrl]);

  if (!config) return null;
  return (
    <DevOverrideProvider>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <UserProvider
          protectedRoutes={[
            '/cam',
            '/settings',
            '/dashboard',
            '/agent',
            '/learn',
            '/tabs',
            '/songs',
            '/tools',
            '/account',
          ]}
        >
          <AppRoutes />
        </UserProvider>
      </ThemeProvider>
    </DevOverrideProvider>
  );
}

export function App() {
  return (
    <AppConfigProvider>
      <AppWithConfig />
    </AppConfigProvider>
  );
}

export default App;

/**
 * Hook (rendered inside <UserProvider>) that:
 *   - keeps analytics auth state in sync with the current user
 *   - fires `page_view` on every route change
 */
function useAnalyticsBootstrap(): void {
  const [userState, userActions] = useUser();
  const location = useLocation();

  // Sync auth mode with the current user state.
  React.useEffect(() => {
    if (userState.data) {
      setAnalyticsAuth(() => userActions.getAuthToken(), userState.data.id);
    } else {
      setAnalyticsAuth(null);
    }
  }, [userState.data, userActions]);

  // Page-view tracking on every route change.
  React.useEffect(() => {
    trackJC('page_view');
  }, [location.pathname, location.search]);
}
